import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { horizontalScrollAnimation } from '#utils';
import s from '#components/navigation/tabs/tabsScroll/tabScroll.module.scss';
import useTranslation from 'next-translate/useTranslation';

type Props = {
  children: ReactNode;
  classes?: string;
  isStyleButton?: boolean;
};

const TabScroll: FC<Props> = ({ classes, isStyleButton, children }) => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [areScrollButtonsVisible, setAreScrollButtonsVisible] = useState(false);
  const [isNextButtonVisible, setIsNextButtonVisible] = useState(true);
  const [isPrevButtonVisible, setIsPrevButtonVisible] = useState(true);
  const [items, setItems] = useState([]);
  const listRef = useRef<HTMLUListElement | undefined>();

  const updateItemMetrics = () => {
    const tabItems = listRef.current?.querySelectorAll('li');
    setItems(
      Array.from(tabItems).map((item) => {
        return {
          xStart: item.offsetLeft,
          xEnd: item.offsetLeft + item.offsetWidth,
        };
      })
    );
  };

  const handleResize = () => {
    updateItemMetrics();
    updateAreScrollButtonsVisible();
  };

  const updateIsPrevButtonVisible = () => {
    setIsPrevButtonVisible(listRef.current?.scrollLeft > 0);
  };

  const updateIsNextButtonVisible = () => {
    if (listRef.current) {
      const listScrollPosition = listRef.current.scrollLeft + 16;
      if (items.length > 0) {
        const posLastListItem = items[items.length - 1].xEnd;
        setIsNextButtonVisible(
          posLastListItem - listScrollPosition >= ref.current?.offsetWidth
        );
      }
    }
  };

  const updateVisibilityForAllButtonsIndividually = () => {
    updateIsNextButtonVisible();
    updateIsPrevButtonVisible();
  };

  const updateAreScrollButtonsVisible = () => {
    if (items.length > 0) {
      const posLastListItem = items[items.length - 1].xEnd;
      setAreScrollButtonsVisible(posLastListItem >= ref.current?.offsetWidth);
    }
    updateVisibilityForAllButtonsIndividually();
  };

  const getIndexAtCurrentScrollPosition = () => {
    const offset = listRef.current?.scrollLeft;
    for (let i = 0; i < items.length; i++) {
      if (offset <= items[i].xStart) {
        return i;
      }
    }
    return 0;
  };

  const scrollToIndex = (index) => {
    // safari can't scroll smoothly with scrollTo function
    if (getComputedStyle(document.body).scrollBehavior === 'smooth') {
      listRef.current.scrollTo({
        left: items[index].xStart,
        behavior: 'smooth',
      });
    } else {
      horizontalScrollAnimation(listRef.current, items[index].xStart, 150);
    }
  };

  const scrollToPrev = () => {
    scrollToIndex(Math.max(0, getIndexAtCurrentScrollPosition() - 1));
  };

  const scrollToNext = () => {
    scrollToIndex(
      Math.min(getIndexAtCurrentScrollPosition() + 1, items?.length - 1)
    );
  };

  useEffect(() => {
    const tabItems = listRef.current?.querySelectorAll(
      '[data-block ="tabsItem"]'
    );
    const getItemMetrics = (listItems) => {
      const newItems = [];
      listItems?.forEach((item) => {
        newItems.push({
          xStart: item.offsetLeft,
          xEnd: item.offsetLeft + item.offsetWidth,
        });
      });

      setItems(newItems);
    };

    getItemMetrics(tabItems);
    updateAreScrollButtonsVisible();

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      /** Fix for memory leak error */
      setItems([]);
      setAreScrollButtonsVisible(false);
      setIsNextButtonVisible(true);
      setIsPrevButtonVisible(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listRef]);

  useEffect(() => {
    updateAreScrollButtonsVisible();
    listRef.current?.addEventListener(
      'scroll',
      updateVisibilityForAllButtonsIndividually
    );

    const currentListRef = listRef.current;
    return () => {
      currentListRef?.removeEventListener(
        'scroll',
        updateVisibilityForAllButtonsIndividually
      );

      /** Fix for memory leak error */
      setAreScrollButtonsVisible(false);
      setIsNextButtonVisible(true);
      setIsPrevButtonVisible(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listRef, updateAreScrollButtonsVisible]);

  const blockClass = classNames(s.host, classes, {
    [s.isScrollLeftOnly]: !isNextButtonVisible,
    [s.isScrollRightOnly]: !isPrevButtonVisible,
    [s.isHidden]: !areScrollButtonsVisible,
  });

  const listContainerClasses = classNames(s.listContainer, {
    [s.isStyleButton]: !!isStyleButton,
  });

  return (
    <div className={blockClass} ref={ref}>
      <div className={listContainerClasses}>
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role */}
        <ul ref={listRef} className={s.listContent} role="tablist">
          {children}
        </ul>
      </div>

      <button
        className={`${s.scrollButton} ${s.isButtonLeft}`}
        onClick={scrollToPrev}
        title={t('ScrollLeft')}
        type="button"
      >
        {t('ScrollLeft')}
      </button>
      <button
        className={`${s.scrollButton} ${s.isButtonRight}`}
        onClick={scrollToNext}
        title={t('ScrollRight')}
        type="button"
      >
        {t('ScrollRight')}
      </button>
    </div>
  );
};

export default TabScroll;
